import { useEffect, useMemo, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import type { ApplicationMetadataFieldUiOptions } from './schema';

type Props = FieldExtensionComponentProps<
  string | undefined,
  ApplicationMetadataFieldUiOptions
>;

type FieldSchema = Omit<Props['schema'], 'enum'> & {
  enum?: string[];
  enumNames?: string[];
  default?: string;
  maxLength?: number;
};

function matchEnumValue(value: string, values: string[], names: string[]) {
  const normalized = value.trim().toLocaleLowerCase();
  const directMatch = values.find(
    option => option.trim().toLocaleLowerCase() === normalized,
  );
  if (directMatch) return directMatch;

  const labelIndex = names.findIndex(
    name => name.trim().toLocaleLowerCase() === normalized,
  );
  return labelIndex >= 0 ? values[labelIndex] : undefined;
}

/**
 * Prefills a template field from the selected catalog entity and keeps it
 * editable.
 */
export const ApplicationMetadataField = (props: Props) => {
  const {
    onChange,
    schema,
    required,
    uiSchema,
    rawErrors,
    formData,
    formContext,
    idSchema,
  } = props;
  const fieldSchema = schema as FieldSchema;
  const options = uiSchema?.['ui:options'];
  const catalogApi = useApi(catalogApiRef);
  const entityRef = options?.entityField
    ? (formContext?.formData?.[options.entityField] as string | undefined)
    : undefined;
  const onChangeRef = useRef(onChange);
  const enumValues = useMemo(() => fieldSchema.enum ?? [], [fieldSchema.enum]);
  const enumNames = useMemo(
    () => fieldSchema.enumNames ?? enumValues,
    [enumValues, fieldSchema.enumNames],
  );
  const fallbackValue = fieldSchema.default;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!entityRef) return undefined;

    let cancelled = false;
    onChangeRef.current(fallbackValue);

    catalogApi
      .getEntityByRef(entityRef)
      .then(entity => {
        if (cancelled || !entity) return;

        let value: string | undefined;
        if (options?.source === 'description') {
          value = entity.metadata.description;
        } else if (options?.annotation) {
          value = entity.metadata.annotations?.[options.annotation];
        }
        const trimmedValue = value?.trim();
        if (!trimmedValue) return;

        const selectedValue =
          enumValues.length > 0
            ? matchEnumValue(trimmedValue, enumValues, enumNames)
            : trimmedValue;
        if (selectedValue) onChangeRef.current(selectedValue);
      })
      .catch(() => {
        // Keep the field editable with its schema fallback if the lookup fails.
      });

    return () => {
      cancelled = true;
    };
  }, [
    catalogApi,
    entityRef,
    enumNames,
    enumValues,
    fallbackValue,
    options?.annotation,
    options?.source,
  ]);

  const selectOptions = useMemo(
    () =>
      enumValues.map((value, index) => ({
        value,
        label: enumNames[index] ?? value,
      })),
    [enumNames, enumValues],
  );

  return (
    <TextField
      id={idSchema?.$id}
      select={selectOptions.length > 0}
      fullWidth
      label={fieldSchema.title}
      value={formData ?? ''}
      onChange={event => onChangeRef.current(event.target.value || undefined)}
      margin="dense"
      variant="outlined"
      required={required}
      error={rawErrors?.length > 0}
      helperText={
        rawErrors?.length ? rawErrors.join(', ') : fieldSchema.description
      }
      multiline={selectOptions.length === 0}
      rows={selectOptions.length === 0 ? options?.rows ?? 5 : undefined}
      inputProps={{ maxLength: fieldSchema.maxLength }}
    >
      {selectOptions.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
